/** @jsxImportSource @emotion/react */
import { createContext, useContext, useEffect, useState } from "react";
import { urlBaseAPI, setUrlPublicaOrganismo } from "../../utils";

/**
 * Carga la estética del organismo antes de renderizar la app.
 *
 * El organismo se resuelve en el backend por el hostname, así que este
 * componente no necesita saber en cuál está: pide /branding y aplica lo que
 * venga.
 *
 * Bloquea el render hasta tener la respuesta a propósito. Si renderizara con
 * los colores por defecto y los cambiara después, se vería el salto de color
 * en cada carga — sobre todo en la pantalla de login, que es lo primero que
 * ve un usuario del organismo.
 *
 * Ante un error NO bloquea: se sigue con los colores por defecto. Que la API
 * de branding falle no puede dejar la app inutilizable.
 */

// Tasa de IVA cuando /branding no la trae: la de Uruguay, que es la que
// estuvo fija en el código hasta que pasó a ser por organismo.
const IVA_POR_DEFECTO = 22;

const EsteticaContext = createContext({
  nombre: null,
  logos: {},
  colores: {},
  funcionalidades: {},
  iva: IVA_POR_DEFECTO
});

export function useEstetica() {
  return useContext(EsteticaContext);
}

/**
 * ¿Está prendida esta funcionalidad para el organismo?
 *
 * Las prende y apaga el superadmin. Ante la duda devuelve false: si /branding
 * falló, es preferible no mostrar una sección que ofrecer algo que el
 * organismo no tiene.
 *
 *   const conUrsea = useFuncionalidad("URSEA");
 */
export function useFuncionalidad(clave) {
  const { funcionalidades } = useContext(EsteticaContext);
  return Boolean(funcionalidades?.[clave]);
}

/**
 * Tasa de IVA del organismo, en porcentaje (22 = 22%). La carga el
 * superadmin. Se usa con conIva() para el total de un monto "más IVA":
 *
 *   const iva = useIva();
 *   formatMonto(conIva(factura.monto, iva));
 */
export function useIva() {
  const { iva } = useContext(EsteticaContext);
  return iva ?? IVA_POR_DEFECTO;
}

/**
 * Nombre del organismo (el que carga el superadmin, llega con /branding), para
 * nombrarlo en textos que lee alguien de afuera, como el cliente: "En revisión
 * por Coleoptera" en vez de "por el organismo". Sin nombre cargado devuelve
 * "el organismo", que se lee bien en las mismas frases (ver deOrganismo).
 *
 *   const organismo = useOrganismo();
 */
export function useOrganismo() {
  const { nombre } = useContext(EsteticaContext);
  return nombre?.trim() || "el organismo";
}

function aplicarColores(colores) {
  if (!colores) return;
  const raiz = document.documentElement;
  const mapa = {
    principal: "--principal",
    acento: "--principalAcento",
    light: "--principalLight"
  };
  for (const [clave, variable] of Object.entries(mapa)) {
    // Solo se pisa lo que el organismo definió; el resto queda en el valor
    // por defecto de App.css.
    if (colores[clave]) raiz.style.setProperty(variable, colores[clave]);
  }
}

// Cómo se llama cada plataforma en el título de la pestaña. Sin esto las
// cuatro apps de un organismo se llaman igual y no se distinguen entre pestañas.
const NOMBRE_APP = {
  PUBLIC: "Portal público",
  ADMIN: "Administración",
  CLIENTE: "Clientes",
  COLABORADOR: "Colaboradores"
};

// Título base de la pestaña: el que aplica Estetica al cargar el branding.
// Las secciones que pisan document.title lo restauran con tituloBase() al
// desmontarse, en vez de hardcodear un nombre.
let tituloBaseActual = "Morfeo";

export function tituloBase() {
  return tituloBaseActual;
}

function aplicarTitulo(nombre, app) {
  if (!nombre) return;
  const plataforma = NOMBRE_APP[app];
  tituloBaseActual = plataforma ? `${nombre} · ${plataforma}` : nombre;
  document.title = tituloBaseActual;
}

function aplicarFavicon(url) {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

// `app` la declara cada aplicación al montar ("PUBLIC" | "ADMIN" | "CLIENTE" |
// "COLABORADOR"): cada build sabe qué plataforma es. Tiene prioridad sobre la
// que deduce el backend por hostname, que en desarrollo es siempre la del
// dominio de la API (localhost) y no distingue desde cuál app se la llama.
export default function Estetica({ app, children }) {
  const [estetica, setEstetica] = useState(null);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    let cancelado = false;

    fetch(`${urlBaseAPI()}/branding`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((datos) => {
        if (cancelado) return;
        // Las imágenes vienen como ruta relativa para no atar la respuesta a
        // un dominio; se completan con el origen de la API.
        const base = urlBaseAPI();
        const absoluta = (ruta) => (ruta ? `${base}${ruta}` : null);
        const resuelta = {
          nombre: datos.nombre,
          // Qué plataforma es esta app: lo que declara la propia app, o lo que
          // deduce el backend por el dominio por el que se entró.
          app: app || datos.app || null,
          // Dominio público del organismo, para linkear al portal desde las
          // otras apps sin hardcodearlo.
          urlPublica: datos.urlPublica || null,
          colores: datos.colores || {},
          logos: {
            color: absoluta(datos.logos?.color),
            blanco: absoluta(datos.logos?.blanco),
            horizontal: absoluta(datos.logos?.horizontal),
            horizontalBlanco: absoluta(datos.logos?.horizontalBlanco)
          },
          favicon: absoluta(datos.favicon),
          // Qué funcionalidades tiene prendidas el organismo. Llegan acá
          // porque /branding se resuelve antes del primer render: así una
          // sección que depende de una funcionalidad no aparece y desaparece.
          funcionalidades: datos.funcionalidades || {},
          iva: datos.iva ?? IVA_POR_DEFECTO
        };
        aplicarColores(resuelta.colores);
        aplicarFavicon(resuelta.favicon);
        aplicarTitulo(resuelta.nombre, resuelta.app);
        setUrlPublicaOrganismo(resuelta.urlPublica);
        setEstetica(resuelta);
        setListo(true);
      })
      .catch(() => {
        // Sin estética: se arranca con los valores por defecto.
        if (!cancelado) setListo(true);
      });

    return () => {
      cancelado = true;
    };
  }, []);

  if (!listo) {
    return (
      <div
        css={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <i
          className="pi pi-spin pi-spinner"
          css={{ fontSize: "2rem", color: "var(--principal)" }}
        />
      </div>
    );
  }

  return (
    <EsteticaContext.Provider
      value={estetica || { nombre: null, colores: {}, logos: {}, funcionalidades: {} }}
    >
      {children}
    </EsteticaContext.Provider>
  );
}
